import { useCallback, useEffect } from 'react'

import {
  useOverlayState,
  removeOverlay,
  setChyron,
} from '../../firebase/hooks/useOverlays'
import { useDebounce } from '../../sharedLegacy/hooks'
import { ChyronType } from '../../firebase/model/chyron-model'
import { usePollsQuery } from '../../Polls/blocs/usePolls'
import type { Poll } from '../../Polls/model/Poll'

/**
 * Accepts both the raw poll shape served by `/api/2/poll/list` (numeric ids and
 * boolean flags) and the object `PollCard` hand-builds from its mapped `Poll`
 * (string ids). `formatPoll` normalises either into the chyron's shape.
 */
type PollOverlayPayload = {
  cardId?: string
  question: string
  opened_date: string
  open_p: number | boolean
  poll_id: string | number
  options: {
    poll_option_id: string | number
    answer_percentage?: string | number
    option: string
  }[]
}

export const toPollOverlayPayload = (
  poll: Poll,
  cardId?: string
): PollOverlayPayload => ({
  cardId,
  question: poll.question,
  opened_date: poll.openedDate,
  open_p: poll.open,
  poll_id: poll.pollId,
  options: poll.options.map((option) => ({
    poll_option_id: option.id,
    answer_percentage: option.answerPercentage,
    option: option.option,
  })),
})

const formatPoll = (poll: PollOverlayPayload) => {
  return {
    cardId: poll.cardId,
    question: poll.question,
    isOpen: !!poll.open_p,
    id: String(poll.poll_id),
    options: poll.options.map((option) => ({
      id: Number(option.poll_option_id),
      answerPercentage: Number(option.answer_percentage ?? 0),
      option: option.option,
    })),
  }
}

const usePollsOverlays = () => {
  const overlay = useOverlayState()
  const activePollId = overlay?.type === ChyronType.PollResult && overlay.id
  const activeOverlayCardId =
    overlay?.type === ChyronType.PollResult && overlay.cardId
  const { data: mainActivePolls } = usePollsQuery({ publicOnly: true })

  const setPollOverlay = useCallback((poll: PollOverlayPayload) => {
    void setChyron({
      type: ChyronType.PollResult,
      ...formatPoll(poll),
    })
  }, [])

  const togglePollOverlay = useCallback(
    (poll: PollOverlayPayload) => {
      if (
        activePollId !== String(poll.poll_id) ||
        activeOverlayCardId !== poll.cardId
      ) {
        setPollOverlay(poll)
      } else {
        void removeOverlay()
      }
    },
    [activeOverlayCardId, activePollId, setPollOverlay]
  )

  const debounceRealTimeUpdate = useDebounce((poll: PollOverlayPayload) => {
    setPollOverlay(poll)
  }, 1000)

  /**
   * TODO (kasperkronborg): I'm not even sure why this is here? Be the looks of
   *  things, this is not called continuously.
   */
  /*
   * Listen for realtime updates on the active polls and update Firebase with lastest data for the selected one in the overlay
   */
  useEffect(() => {
    const activeOverlayPoll = mainActivePolls?.find(
      (poll) => String(poll.pollId) === activePollId
    )

    if (overlay?.type !== ChyronType.PollResult || !activeOverlayPoll) {
      debounceRealTimeUpdate.cancel()
      return
    }

    debounceRealTimeUpdate(
      toPollOverlayPayload(activeOverlayPoll, activeOverlayCardId || undefined)
    )
  }, [
    activeOverlayCardId,
    activePollId,
    debounceRealTimeUpdate,
    mainActivePolls,
    overlay?.type,
  ])

  return { togglePollOverlay, overlayPollId: activePollId }
}

// oxlint-disable-next-line import/no-default-export
export default usePollsOverlays
